import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import FAQAccordion from "@/components/FAQAccordion";
import RelatedBlock from "@/components/RelatedBlock";
import ServiceCard from "@/components/cards/ServiceCard";
import BaseCard from "@/components/cards/BaseCard";
import TownLocalBusinessSchema from "@/components/schemas/TownLocalBusinessSchema";
import { COMPANY } from "@/data/company";
import { ALL_SERVICES, TOWNS, REGIONS } from "@/data/nav";
import { trackPhoneClick } from "@/lib/track";

/**
 * TownCoverageTemplate — leaner /coverage/:slug layout for individual towns.
 * Kept lighter than the region hub (500–700 words) since towns are one rung
 * below regions in the coverage hierarchy.
 */
const TownCoverageTemplate = ({ data }) => {
  const services = data.servicesFocus
    .map((slug) => ALL_SERVICES.find((s) => s.slug === slug))
    .filter(Boolean);

  const nearbyTowns = TOWNS.filter(
    (t) => t.slug !== data.slug && (t.region === data.region || REGIONS.find((r) => r.slug === t.region))
  )
    .filter((t) => t.region === data.region)
    .slice(0, 4);

  return (
    <div data-testid={`town-page-${data.slug}`}>
      <Seo
        title={data.seo.title}
        description={data.seo.description}
        path={`/coverage/${data.slug}`}
      />
      <TownLocalBusinessSchema
        townSlug={data.slug}
        townName={data.name}
        regionName={data.regionName}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <PhotoBackdrop src={data.heroImage} position={data.heroPosition} />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 relative">
          <p className="eyebrow">
            Coverage ·{" "}
            <Link
              to={`/coverage/${data.region}`}
              className="text-[color:var(--lt-blue-bright)] hover:underline"
              data-testid="town-parent-region-link"
            >
              {data.regionName}
            </Link>{" "}
            · {data.name}
          </p>
          <h1
            className="font-display mt-6 text-white max-w-4xl"
            style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1 }}
            data-testid="town-h1"
          >
            {data.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[color:var(--lt-text-dim)]">
            {data.subhead}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={COMPANY.phone.tel}
              onClick={() => trackPhoneClick(`town_${data.slug}_hero`)}
              className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="town-hero-call"
            >
              <Phone size={16} /> Call an engineer for {data.name}
            </a>
            <Link
              to="/contact"
              className="btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="town-hero-enquire"
            >
              Send an enquiry <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <Breadcrumb
        items={[
          { label: "Coverage", to: "/coverage" },
          { label: data.regionName, to: `/coverage/${data.region}` },
          { label: data.name },
        ]}
      />

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="eyebrow">The town, in plain terms</p>
          <h2
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
          >
            Engineering support in {data.name}.
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-[15.5px] leading-relaxed text-[color:var(--lt-text-dim)] whitespace-pre-line">
            {data.intro}
          </p>
        </div>
      </section>

      {/* TYPICAL WORK */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">What we typically do in {data.name}</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
        >
          Common {data.name} work.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.typicalWork.map((item, i) => (
            <div
              key={i}
              className="glass-card rounded-lg p-5 flex gap-3"
              data-testid={`town-work-${i}`}
            >
              <Check
                size={18}
                className="mt-1 shrink-0 text-[color:var(--lt-blue-bright)]"
              />
              <p className="text-[14.5px] leading-relaxed text-[color:var(--lt-text-dim)]">
                {item}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES FOCUS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Most-requested services in {data.name}</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
        >
          Services relevant to this coverage area.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((s) => (
            <ServiceCard
              key={s.slug}
              slug={s.slug}
              name={s.name}
              blurb={s.blurb}
              categoryName={s.categoryName}
            />
          ))}
        </div>
        <div className="mt-6">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[13.5px] text-[color:var(--lt-blue-bright)] font-semibold"
          >
            View all services <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* COVERAGE NOTE + NEARBY TOWNS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="glass-card rounded-xl p-8 lg:p-10">
          <p className="eyebrow">How we cover {data.name}</p>
          <h2
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(24px, 2.8vw, 34px)", lineHeight: 1.05 }}
          >
            Reaching {data.name} from Northwich.
          </h2>
          <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
            {data.coverageNote}
          </p>

          {nearbyTowns.length > 0 && (
            <>
              <p className="mt-8 text-[12px] font-semibold uppercase tracking-[0.24em] text-[color:var(--lt-text-mute)]">
                Nearby towns we also cover
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {nearbyTowns.map((t) => (
                  <Link
                    key={t.slug}
                    to={`/coverage/${t.slug}`}
                    className="btn-ghost rounded-full px-4 py-2 text-[13px] font-semibold inline-flex items-center gap-1.5"
                    data-testid={`town-nearby-${t.slug}`}
                  >
                    {t.name} <ChevronRight size={13} />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow">{data.name} FAQ</p>
            <h2
              className="font-display mt-4 text-white"
              style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
            >
              Common questions.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={data.faq} testIdPrefix={`town-faq-${data.slug}`} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTABanner
        variant="standard"
        title={`Need an engineer in ${data.name}? Let's talk.`}
        body="Give us a short outline of what you're trying to solve. We'll come back with an honest view on whether we're the right fit and, if we are, a fixed-price way forward."
        phoneLocation={`town_${data.slug}_bottom`}
      />

      {/* RELATED — parent region + nearby towns */}
      <RelatedBlock
        title="Explore related coverage"
        groups={[
          {
            eyebrow: "Region",
            items: [
              <BaseCard
                key={data.region}
                to={`/coverage/${data.region}`}
                eyebrow="Region hub"
                title={data.regionName}
                blurb={`See our wider ${data.regionName} coverage — including neighbouring towns and industrial districts.`}
                testid={`town-related-region-${data.region}`}
              />,
            ],
          },
        ]}
      />
    </div>
  );
};

export default TownCoverageTemplate;
